import { debounce } from 'lodash';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useGetOrganisationJobsQuery } from 'src/services/jobs/jobs.service';

import { MentionJob } from 'src/components/hook-form/rhf-mention-text-field';

import { TenantType } from 'src/types/enums';

// ----------------------------------------------------------------------

const NO_JOBS: MentionJob[] = [];

/**
 * The job search behind `#` tags in a note (JT1-1738). Wire its result straight into
 * RHFMentionTextField: the field reports what is typed after an open `#`, and gets the matching jobs
 * back. Nothing is fetched until a `#` is open. `/jobs` leaves archived jobs and closed roles out.
 */
export function useJobTagSearch() {
  const tenantType = useOrgTenant();

  const [query, setQuery] = useState<string | null>(null);
  const [searchedQuery, setSearchedQuery] = useState<string | null>(null);

  const search = useMemo(() => debounce((value: string) => setSearchedQuery(value), 300), []);

  useEffect(() => () => search.cancel(), [search]);

  const onJobQueryChange = useCallback((value: string | null) => {
    setQuery(value);

    if (value === null) {
      search.cancel();
      setSearchedQuery(null);
    } else {
      search(value);
    }
  }, [search]);

  const { currentData, isFetching } = useGetOrganisationJobsQuery(
    { search_query: searchedQuery ?? '', page: 1, per_page: 8 },
    { skip: searchedQuery === null }
  );

  const jobs = useMemo<MentionJob[]>(() => (currentData?.results ?? []).map((job) => ({
    id: job.id as string,
    title: job.title,
    // A recruiter runs the same role for several clients; the company tells them apart.
    subtitle: tenantType === TenantType.Recruiter ? job.client_company_name : undefined,
  })), [currentData, tenantType]);

  return {
    onJobQueryChange,
    jobs: query === null ? NO_JOBS : jobs,
    jobsLoading: query !== null && (isFetching || query !== searchedQuery),
  };
}
